import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { 
  CreditCard, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search,
  RefreshCw,
  Download,
  Filter,
  IndianRupee,
  Smartphone,
  Banknote,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Plus,
  CalendarIcon,
} from "lucide-react";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subWeeks, subMonths } from "date-fns";

const PAYMENT_METHODS = [
  { id: "cash", name: "Cash" },
  { id: "upi", name: "UPI" },
  { id: "bank_transfer", name: "Bank Transfer" },
  { id: "razorpay", name: "Razorpay" },
  { id: "wallet", name: "Wallet" },
  { id: "other", name: "Other" },
];

const PERIOD_SHORTCUTS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "this_week", label: "This Week" },
  { id: "last_week", label: "Last Week" },
  { id: "this_month", label: "This Month" },
  { id: "last_month", label: "Last Month" },
  { id: "custom", label: "Custom Range" },
];

export const PaymentReportsPage = ({ user }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    pending: { count: 0, amount: 0 },
    approved: { count: 0, amount: 0 },
    rejected: { count: 0, amount: 0 }
  });
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(20);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [cities, setCities] = useState([]);
  
  // Period filter
  const [periodFilter, setPeriodFilter] = useState("this_month");
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return { from: startOfMonth(now), to: endOfMonth(now) };
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'recorded_at', direction: 'desc' });
  
  // Modals
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [processingAction, setProcessingAction] = useState(false);
  
  // Record Payment Dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverSearch, setDriverSearch] = useState("");
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "cash",
    reference_number: "",
    notes: ""
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Fetch cities
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await axios.get(`${API}/cities`);
        // Sort cities alphabetically
        const sortedCities = (res.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCities(sortedCities);
      } catch (err) {
        // Error fetching cities
      }
    };
    fetchCities();
  }, []);

  // Handle period change
  useEffect(() => {
    const now = new Date();
    let from, to;
    
    switch (periodFilter) {
      case "today":
        from = startOfDay(now);
        to = endOfDay(now);
        break;
      case "yesterday":
        const yesterday = subDays(now, 1);
        from = startOfDay(yesterday);
        to = endOfDay(yesterday);
        break;
      case "this_week":
        from = startOfWeek(now, { weekStartsOn: 1 });
        to = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case "last_week":
        const lastWeek = subWeeks(now, 1);
        from = startOfWeek(lastWeek, { weekStartsOn: 1 });
        to = endOfWeek(lastWeek, { weekStartsOn: 1 });
        break;
      case "this_month":
        from = startOfMonth(now);
        to = endOfMonth(now);
        break;
      case "last_month":
        const lastMonth = subMonths(now, 1);
        from = startOfMonth(lastMonth);
        to = endOfMonth(lastMonth);
        break;
      case "custom":
        // Keep existing custom range
        return;
      default:
        from = startOfMonth(now);
        to = endOfMonth(now);
    }
    
    setDateRange({ from, to });
  }, [periodFilter]);

  const fetchPayments = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", limit);
      params.append("offset", offset);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (paymentTypeFilter) params.append("payment_type", paymentTypeFilter);
      if (paymentMethodFilter && paymentMethodFilter !== "all") params.append("payment_method", paymentMethodFilter);
      if (cityFilter && cityFilter !== "all") params.append("city_id", cityFilter);
      if (dateRange.from) params.append("from_date", dateRange.from.toISOString());
      if (dateRange.to) params.append("to_date", dateRange.to.toISOString());
      
      const res = await axios.get(`${API}/admin/${user.id}/all-payments?${params}`);
      setPayments(res.data.payments || []);
      setTotal(res.data.total || 0);
      setSummary(res.data.summary || {
        pending: { count: 0, amount: 0 },
        approved: { count: 0, amount: 0 },
        rejected: { count: 0, amount: 0 }
      });
    } catch (err) {
      toast.error("Failed to load payments");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, offset, limit, statusFilter, paymentTypeFilter, paymentMethodFilter, cityFilter, dateRange]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Fetch drivers for record payment
  const fetchDrivers = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/admin/drivers?admin_id=${user.id}`);
      setDrivers(res.data || []);
    } catch (err) {
      // Error fetching drivers
    }
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchPayments, 30000);
    return () => clearInterval(interval);
  }, [fetchPayments]);

  const handleApprove = async (paymentId) => {
    setProcessingAction(true);
    try {
      await axios.post(`${API}/admin/${user.id}/payments/${paymentId}/approve`);
      toast.success("Payment approved!");
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve payment");
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReject = async () => {
    if (!selectedPayment) return;
    
    setProcessingAction(true);
    try {
      await axios.post(
        `${API}/admin/${user.id}/payments/${selectedPayment.id}/reject?reason=${encodeURIComponent(rejectReason)}`
      );
      toast.success("Payment rejected");
      setShowRejectModal(false);
      setRejectReason("");
      setSelectedPayment(null);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject payment");
    } finally {
      setProcessingAction(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select payments to approve");
      return;
    }
    
    setProcessingAction(true);
    try {
      const res = await axios.post(`${API}/admin/${user.id}/payments/bulk-approve`, selectedIds);
      toast.success(res.data.message);
      setSelectedIds([]);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to bulk approve");
    } finally {
      setProcessingAction(false);
    }
  };

  const toggleSelectAll = () => {
    const pendingPayments = payments.filter(p => p.status === "pending");
    if (selectedIds.length === pendingPayments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingPayments.map(p => p.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case "razorpay":
      case "razorpay_mock":
        return <CreditCard className="w-4 h-4 text-blue-500" />;
      case "upi":
        return <Smartphone className="w-4 h-4 text-purple-500" />;
      case "cash":
        return <Banknote className="w-4 h-4 text-green-500" />;
      default:
        return <IndianRupee className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Sortable header
  const SortableHeader = ({ label, sortKey }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50"
      onClick={() => setSortConfig(prev => ({
        key: sortKey,
        direction: prev.key === sortKey && prev.direction === 'asc' ? 'desc' : 'asc'
      }))}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === sortKey ? (
          sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
        ) : <ArrowUpDown className="w-4 h-4 opacity-30" />}
      </div>
    </TableHead>
  );

  // Sort and filter payments
  const processedPayments = payments
    .filter(p => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        p.driver_name?.toLowerCase().includes(query) ||
        p.driver_phone?.includes(query) ||
        p.reference_number?.toLowerCase().includes(query) ||
        p.city_name?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      const { key, direction } = sortConfig;
      let aVal = a[key];
      let bVal = b[key];
      
      if (key === 'recorded_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (key === 'amount') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

  const pendingCount = payments.filter(p => p.status === "pending").length;
  
  // Open record payment dialog
  const openRecordPayment = () => {
    fetchDrivers();
    setSelectedDriver(null);
    setPaymentForm({
      amount: "",
      payment_method: "cash",
      reference_number: "",
      notes: ""
    });
    setShowPaymentDialog(true);
  };
  
  // Submit payment
  const handleSubmitPayment = async () => {
    if (!selectedDriver || !paymentForm.amount) {
      toast.error("Please select a driver and enter an amount");
      return;
    }
    
    setSubmittingPayment(true);
    try {
      await axios.post(`${API}/admin/payments`, {
        admin_id: user.id,
        payment_type: "driver_to_admin",
        recipient_id: selectedDriver.id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number || null,
        notes: paymentForm.notes || null
      });
      
      toast.success("Payment recorded successfully");
      setShowPaymentDialog(false);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setSubmittingPayment(false);
    }
  };

  // Filtered drivers for search
  const filteredDrivers = drivers.filter(d => {
    if (!driverSearch) return true;
    const search = driverSearch.toLowerCase();
    return d.name?.toLowerCase().includes(search) || d.phone?.includes(search);
  });

  return (
    <div className="space-y-6" data-testid="payment-reports-page">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending</p>
              <p className="text-xl font-bold text-amber-600">{summary.pending.count}</p>
              <p className="text-xs text-muted-foreground">{formatCurrency(summary.pending.amount)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Approved</p>
              <p className="text-xl font-bold text-green-600">{summary.approved.count}</p>
              <p className="text-xs text-muted-foreground">{formatCurrency(summary.approved.amount)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Rejected</p>
              <p className="text-xl font-bold text-red-600">{summary.rejected.count}</p>
              <p className="text-xs text-muted-foreground">{formatCurrency(summary.rejected.amount)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Collected</p>
              <p className="text-xl font-bold text-blue-600">{formatCurrency(summary.approved.amount)}</p>
              <p className="text-xs text-muted-foreground">{summary.approved.count} payments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-card rounded-xl border p-4">
        <div className="flex flex-col gap-4">
          {/* Top row - Search and Record Payment */}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, phone, city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
            
            <div className="flex gap-2">
              <Button variant="default" onClick={openRecordPayment}>
                <Plus className="w-4 h-4 mr-2" />
                Record Payment
              </Button>
              {selectedIds.length > 0 && (
                <Button 
                  onClick={handleBulkApprove} 
                  disabled={processingAction}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve ({selectedIds.length})
                </Button>
              )}
              <Button variant="outline" onClick={fetchPayments} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Filter row */}
          <div className="flex flex-wrap gap-3 items-center">
            {/* Period Filter */}
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger className="w-36">
                <CalendarIcon className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_SHORTCUTS.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Custom date picker */}
            {periodFilter === "custom" && (
              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-64 justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "dd MMM")} - {format(dateRange.to, "dd MMM yyyy")}
                        </>
                      ) : (
                        format(dateRange.from, "dd MMM yyyy")
                      )
                    ) : (
                      "Pick a date range"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={dateRange}
                    onSelect={(range) => {
                      if (range?.from && range?.to) {
                        setDateRange({ from: range.from, to: range.to });
                        setShowDatePicker(false);
                      } else if (range?.from) {
                        setDateRange({ from: range.from, to: range.from });
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            )}
            
            {/* City Filter */}
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All Methods" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                <SelectItem value="razorpay">Razorpay</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Date range display */}
            {periodFilter !== "custom" && dateRange.from && (
              <span className="text-sm text-muted-foreground">
                {format(dateRange.from, "dd MMM")} - {format(dateRange.to, "dd MMM yyyy")}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <input
                  type="checkbox"
                  checked={selectedIds.length === payments.filter(p => p.status === "pending").length && pendingCount > 0}
                  onChange={toggleSelectAll}
                  className="rounded"
                  disabled={pendingCount === 0}
                />
              </TableHead>
              <SortableHeader label="Rider" sortKey="driver_name" />
              <SortableHeader label="City" sortKey="city_name" />
              <SortableHeader label="Amount" sortKey="amount" />
              <TableHead>Type</TableHead>
              <SortableHeader label="Method" sortKey="payment_method" />
              <TableHead>Reference</TableHead>
              <SortableHeader label="Date" sortKey="recorded_at" />
              <SortableHeader label="Status" sortKey="status" />
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground mt-2">Loading payments...</p>
                </TableCell>
              </TableRow>
            ) : processedPayments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  <CreditCard className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No payments found</p>
                </TableCell>
              </TableRow>
            ) : (
              processedPayments.map((payment) => (
                <TableRow key={payment.id} className={payment.status === "pending" ? "bg-amber-50/50" : ""}>
                  <TableCell>
                    {payment.status === "pending" && (
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(payment.id)}
                        onChange={() => toggleSelect(payment.id)}
                        className="rounded"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{payment.driver_name}</p>
                      <p className="text-xs text-muted-foreground">{payment.driver_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{payment.city_name || '-'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-semibold text-green-600">{formatCurrency(payment.amount)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs capitalize">
                      {payment.payment_type?.replace(/_/g, ' ')}
                    </span>
                    {payment.subscription_type && (
                      <p className="text-xs text-muted-foreground">
                        {payment.subscription_type.replace(/_/g, ' ')}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getPaymentMethodIcon(payment.payment_method)}
                      <span className="text-xs capitalize">{payment.payment_method?.replace(/_/g, ' ')}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs font-mono">
                      {payment.reference_number || payment.razorpay_order_id?.slice(0, 12) || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs">
                      <p>{new Date(payment.recorded_at).toLocaleDateString('en-IN')}</p>
                      <p className="text-muted-foreground">{new Date(payment.recorded_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(payment.status)}</TableCell>
                  <TableCell className="text-right">
                    {payment.status === "pending" && (
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleApprove(payment.id)}
                          disabled={processingAction}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowRejectModal(true);
                          }}
                          disabled={processingAction}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {payment.status === "rejected" && payment.rejection_reason && (
                      <span className="text-xs text-red-600" title={payment.rejection_reason}>
                        {payment.rejection_reason.slice(0, 20)}...
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} payments
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Reject payment of <strong>{formatCurrency(selectedPayment?.amount)}</strong> from <strong>{selectedPayment?.driver_name}</strong>?
            </p>
            <Input
              placeholder="Reason for rejection (optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={processingAction}
            >
              {processingAction ? "Rejecting..." : "Reject Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Driver selection */}
            <div className="space-y-2">
              <Label>Select Driver</Label>
              <Input
                placeholder="Search driver by name or phone..."
                value={driverSearch}
                onChange={(e) => setDriverSearch(e.target.value)}
              />
              {!selectedDriver && (
                <div className="max-h-40 overflow-y-auto border rounded-md">
                  {filteredDrivers.slice(0, 10).map(driver => (
                    <div
                      key={driver.id}
                      className="p-2 hover:bg-muted cursor-pointer border-b last:border-b-0"
                      onClick={() => {
                        setSelectedDriver(driver);
                        setDriverSearch("");
                      }}
                    >
                      <p className="font-medium text-sm">{driver.name}</p>
                      <p className="text-xs text-muted-foreground">{driver.phone} • {driver.vehicle_type || '-'}</p>
                    </div>
                  ))}
                  {filteredDrivers.length === 0 && (
                    <p className="p-2 text-sm text-muted-foreground">No drivers found</p>
                  )}
                </div>
              )}
              {selectedDriver && (
                <div className="bg-muted/50 rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <p className="font-medium">{selectedDriver.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedDriver.phone}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedDriver(null)}>
                    Change
                  </Button>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(v) => setPaymentForm(prev => ({ ...prev, payment_method: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reference Number (Optional)</Label>
              <Input
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, reference_number: e.target.value }))}
                placeholder="Transaction ID or reference"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitPayment} disabled={submittingPayment || !selectedDriver}>
              {submittingPayment ? "Recording..." : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentReportsPage;
